import { useState, useRef, useEffect } from "react";

import style from "../style/components/Buttons.module.css";

const BlockButton = ({
    type = "Primary",
    text,
    loading = false,
    onClick,
}: {
    type?: "Primary" | "Delete" | "DeleteFill" | "Default";
    text: string;
    loading?: boolean;
    onClick: () => void;
}) => {
    const [hovered, setHovered] = useState<boolean>(false);
    const [clicked, setClicked] = useState<boolean>(false);

    return (
        <div
            className={[
                style["container--blockButton"],
                // color by type
                type === "Primary" && style["container--blockButton--primary"],
                type === "Delete" && style["container--blockButton--delete"],
                type === "DeleteFill" && style["container--blockButton--delete-fill"],
                type === "Default" && style["container--blockButton--default"],
                // click event
                clicked && style["container--blockButton--clicked"],
            ].join(" ")}
        >
            <button
                onClick={onClick}
                // hover events
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => {
                    setHovered(false);
                    setClicked(false);
                }}
                // click events
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--blockButton"],
                    hovered && style["button--blockButton--hovered"],
                    clicked && style["button--blockButton--clicked"],
                    loading && style["loading--default"],
                ].join(" ")}
            >
                {text}
            </button>
        </div>
    );
};

const ConfirmButton = ({
    type = "Default",
    text,
    loading = false,
    onClick,
}: {
    type?: "Default" | "Delete";
    text: string;
    loading?: boolean;
    onClick: () => void;
}) => {
    const container = useRef<HTMLDivElement>(null);
    const [hovered, setHovered] = useState<boolean>(false);
    const [clicked, setClicked] = useState<boolean>(false);

    const [toggled, setToggled] = useState<boolean>(false);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!container.current?.contains(e.target as Node)) {
                if (toggled) {
                    setToggled(false);
                }
            }
        };

        if (toggled) {
            document.body.addEventListener("click", handleClickOutside);
        }

        return () => {
            document.body.removeEventListener("click", handleClickOutside);
        };
    }, [toggled]);

    return (
        <div
            ref={container}
            className={[
                style["container--blockButton"],
                // color by type
                !toggled && style["container--blockButton--confirm"],
                toggled && style["container--blockButton--confirm-fill"],

                type === "Delete" && !toggled && style["container--blockButton--delete"],
                type === "Delete" && toggled && style["container--blockButton--delete-fill"],

                // click event
                clicked && style["container--blockButton--clicked"],
            ].join(" ")}
        >
            <button
                onClick={() => {
                    if (!toggled) {
                        setToggled(true);
                    } else {
                        onClick();
                        setToggled(false);
                    }
                }}
                // hover events
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => {
                    setHovered(false);
                    setClicked(false);
                }}
                // click events
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--blockButton"],
                    hovered && style["button--blockButton--hovered"],
                    clicked && style["button--blockButton--clicked"],
                    loading && style["loading--default"],
                ].join(" ")}
            >
                {toggled ? `${text} 승인하기` : `${text} 처리하기`}
            </button>
        </div>
    );
};

const ListButton = ({
    type = "Primary",
    text,
    loading = false,
    onClick,
}: {
    type?: "Primary" | "Default";
    text: string;
    loading?: boolean;
    onClick: () => void;
}) => {
    const [hovered, setHovered] = useState<boolean>(false);
    const [clicked, setClicked] = useState<boolean>(false);

    return (
        <div
            className={[
                style["container--listButton"],
                // color by type
                type === "Primary" && style["container--listButton--primary"],
                type === "Default" && style["container--listButton--default"],
                // click event
                clicked && style["container--listButton--clicked"],
            ].join(" ")}
        >
            <button
                onClick={onClick}
                // hover events
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => {
                    setHovered(false);
                    setClicked(false);
                }}
                // click events
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--listButton"],
                    hovered && style["button--listButton--hovered"],
                    clicked && style["button--listButton--clicked"],
                    loading && style["loading--default"],
                ].join(" ")}
            >
                {text}
            </button>
        </div>
    );
};

const TextButton = ({
    type = "Primary",
    size = "Default",
    text,
    onClick,
}: {
    type?: "Primary" | "Caution";
    size?: "Default";
    text: string;
    onClick: () => void;
}) => {
    const [hovered, setHovered] = useState<boolean>(false);
    const [clicked, setClicked] = useState<boolean>(false);

    return (
        <div
            className={[
                style["container--textButton"],
                // color by type
                type === "Primary" && style["container--textButton--primary"],
                type === "Caution" && style["container--textButton--caution"],
                // click event
                clicked && style["container--textButton--clicked"],
            ].join(" ")}
        >
            <button
                onClick={onClick}
                // hover events
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => {
                    setHovered(false);
                    setClicked(false);
                }}
                // click events
                onMouseDown={() => setClicked(true)}
                onMouseUp={() => setClicked(false)}
                onTouchStart={() => setClicked(true)}
                onTouchEnd={() => setClicked(false)}
                className={[
                    style["button--textButton"],
                    // hover
                    hovered && style["button--textButton--hovered"],
                    // size
                    size === "Default" && style["button--textButton--default"],
                ].join(" ")}
            >
                {text}
            </button>
        </div>
    );
};

export { BlockButton, ConfirmButton, ListButton, TextButton };
